import React from 'react';
import { motion } from 'framer-motion';
import { Database, Shield, Clock, Share2, Cookie, UserCheck, Lock, Globe, Mail, Info } from 'lucide-react';

const RGPD = () => {
  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Sections with icons
  const sections = [
    {
      title: "Collecte des données personnelles",
      icon: <Database className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Nous pouvons collecter les informations personnelles suivantes :</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
            <li>Nom et prénom</li>
            <li>Adresse email</li>
            <li>Numéro de téléphone</li>
            <li>Adresse postale</li>
            <li>Informations de navigation (cookies, adresse IP)</li>
            <li>Données relatives à votre formation ou votre parcours professionnel</li>
          </ul>
        </>
      )
    },
    {
      title: "Utilisation des données",
      icon: <Info className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Nous utilisons vos données personnelles pour les finalités suivantes :</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
            <li>Gérer votre inscription à nos formations</li>
            <li>Vous fournir les informations et services demandés</li>
            <li>Communiquer avec vous concernant nos actualités et événements</li>
            <li>Améliorer nos services et l'expérience utilisateur</li>
            <li>Respecter nos obligations légales et réglementaires</li>
          </ul>
        </>
      )
    },
    {
      title: "Base légale du traitement",
      icon: <Shield className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Le traitement de vos données personnelles est fondé sur :</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
            <li>Votre consentement</li>
            <li>L'exécution d'un contrat auquel vous êtes partie</li>
            <li>Le respect de nos obligations légales</li>
            <li>Notre intérêt légitime à développer et promouvoir nos activités</li>
          </ul>
        </>
      )
    },
    {
      title: "Conservation des données",
      icon: <Clock className="text-blue-400" size={24} />,
      content: "Nous conservons vos données personnelles aussi longtemps que nécessaire pour atteindre les finalités pour lesquelles elles ont été collectées, sauf si une période de conservation plus longue est requise ou permise par la loi."
    },
    {
      title: "Partage des données",
      icon: <Share2 className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Nous ne vendons pas vos données personnelles à des tiers. Nous pouvons partager vos informations avec :</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
            <li>Nos partenaires de formation</li>
            <li>Des prestataires de services qui nous aident à exploiter notre site et nos services</li>
            <li>Les autorités publiques lorsque la loi l'exige</li>
          </ul>
          <p className="mt-2">Tous les tiers avec lesquels nous partageons vos données sont tenus de respecter la confidentialité et la sécurité de vos informations.</p>
        </>
      )
    },
    {
      title: "Cookies et technologies similaires",
      icon: <Cookie className="text-blue-400" size={24} />,
      content: "Notre site utilise des cookies et technologies similaires pour améliorer votre expérience de navigation, analyser l'utilisation du site et personnaliser le contenu. Vous pouvez configurer votre navigateur pour refuser tous les cookies ou vous alerter lorsque des cookies sont envoyés."
    },
    {
      title: "Vos droits",
      icon: <UserCheck className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
            <li>Droit d'accès à vos données</li>
            <li>Droit de rectification des données inexactes</li>
            <li>Droit à l'effacement (droit à l'oubli)</li>
            <li>Droit à la limitation du traitement</li>
            <li>Droit à la portabilité des données</li>
            <li>Droit d'opposition au traitement</li>
            <li>Droit de ne pas faire l'objet d'une décision fondée exclusivement sur un traitement automatisé</li>
          </ul>
          <p className="mt-2">Pour exercer ces droits, veuillez nous contacter aux coordonnées indiquées ci-dessous.</p>
        </>
      )
    },
    {
      title: "Sécurité des données",
      icon: <Lock className="text-blue-400" size={24} />,
      content: "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre la perte, l'accès non autorisé, la divulgation, l'altération ou la destruction."
    },
    {
      title: "Transferts internationaux de données",
      icon: <Globe className="text-blue-400" size={24} />,
      content: "Vos données personnelles peuvent être transférées et traitées dans des pays autres que celui où vous résidez. Ces pays peuvent avoir des lois différentes en matière de protection des données. Lorsque nous transférons vos données en dehors de l'Espace Économique Européen, nous nous assurons qu'un niveau de protection adéquat est mis en place."
    },
    {
      title: "Contact",
      icon: <Mail className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Si vous avez des questions concernant cette politique de confidentialité ou si vous souhaitez exercer vos droits, veuillez nous contacter à :</p>
          <address className="mt-2 not-italic text-gray-300">
            <p>CFA Est Loiret</p>
            <p>3 rue Cyrille Robert</p>
            <p>45200 Montargis</p>
            <p>Email : contact@cfa-montargis.com</p>
            <p>Téléphone : 02 38 98 05 92</p>
          </address>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white pt-24">
      <div className="container mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4 text-white">
            Politique de confidentialité (RGPD)
          </h1>
          <div className="h-1 w-24 bg-blue-500 mx-auto rounded-full"></div>
          <p className="mt-6 text-gray-300 max-w-2xl mx-auto">
            La présente politique de confidentialité décrit comment le CFA Est Loiret collecte, utilise et protège vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).
          </p>
        </motion.div>
        
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl mx-auto"
        >
          {sections.map((section, index) => (
            <motion.div 
              key={index}
              variants={item}
              className="mb-12 bg-slate-800/50 p-6 rounded-lg border border-slate-700 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-900/10 transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-slate-700/50 rounded-lg mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-semibold text-white">
                  {index + 1}. {section.title}
                </h2>
              </div>
              <div className="pl-14 text-gray-300 leading-relaxed">
                {section.content}
              </div>
            </motion.div>
          ))}
          
          <motion.div 
            variants={item}
            className="mt-16 text-center text-gray-400 italic"
          >
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default RGPD;